import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { SwipeableDrawer, Box, Button, IconButton, Tooltip, Chip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
// components
import { TaskSidebar } from '../../../components/dashboardSidebars';
import { Iconify } from '../../../components';

// ----------------------------------------------------------------------

export default function ReusableSidebar({
    type,
    open,
    onClose,
    data,
    getReminderBtn,
    handleFollowUp,
    handleSendWhatsApp,
    handleAttendClick,
    handleAttendFailedClick,
    reminderDataId,
    overViewbtns,
    deleteTask,
    setReloadCounters,
    parentType,
    onTaskUpdate,
    isLegacy,
}) {
    const theme = useTheme();
    const navigate = useNavigate();
    const { practiceId: practiceIdParam } = useParams();
    const resolveLeadId = (lead) => {
        if (lead?.id) return lead.id;
        if (typeof lead === 'number') return lead;
        if (typeof lead === 'string' && /^\d+$/.test(lead)) return Number(lead);
        return null;
    };
    const leadId = resolveLeadId(data?.lead);
    const practiceId = practiceIdParam || data?.lead_treatment?.treatment_data?.practice || null;
    return (
        <SwipeableDrawer
            anchor="right"
            open={open}
            onClose={onClose}
            BackdropProps={{ invisible: true }}
            PaperProps={{
                sx: {
                    width: '420px',
                    maxWidth: '85%',
                    backgroundImage: 'none !important',
                    borderLeft: 'none',
                    boxShadow: '-8px 0 24px rgba(0,0,0,0.08)',
                }
            }}
            sx={{ zIndex: 1116 }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Top accent line */}
                <Box sx={{ height: 3, background: 'linear-gradient(90deg, #00A76F 0%, #00C897 50%, #5BE49B 100%)' }} />

                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2.5,
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Tooltip title="Delete task">
                            <IconButton
                                size="small"
                                onClick={() => deleteTask?.(data)}
                                sx={{
                                    color: 'text.secondary',
                                    '&:hover': { color: 'error.main', bgcolor: alpha('#FF5630', 0.08) },
                                }}
                            >
                                <Iconify icon="ri:delete-bin-line" width={18} />
                            </IconButton>
                        </Tooltip>
                        {leadId && practiceId && (
                            <Tooltip title="Open lead detail">
                                <Button
                                    size="small"
                                    variant="text"
                                    startIcon={<Iconify icon="eva:external-link-fill" width={16} />}
                                    onClick={() => { onClose(); navigate(`/dashboard/practice/${practiceId}/lead-detail/${leadId}`); }}
                                    sx={{
                                        color: 'text.secondary',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: '0.8rem',
                                        px: 1,
                                        py: 0.5,
                                        minWidth: 0,
                                        '&:hover': { color: 'primary.main', bgcolor: alpha('#00A76F', 0.08) },
                                    }}
                                >
                                    Go to lead
                                </Button>
                            </Tooltip>
                        )}
                        {isLegacy && (
                            <Tooltip title="Imported from the original To-Do list. New tasks use the V2 format.">
                                <Chip
                                    label="Legacy"
                                    size="small"
                                    sx={{
                                        bgcolor: alpha(theme.palette.warning.main, 0.12),
                                        color: theme.palette.warning.main,
                                        fontWeight: 600,
                                        fontSize: '0.65rem',
                                        height: 20,
                                        cursor: 'default',
                                        '& .MuiChip-label': { px: 0.75 },
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Box>
                    <Tooltip title="Close">
                        <IconButton
                            size="small"
                            onClick={onClose}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': { bgcolor: 'action.hover' },
                            }}
                        >
                            <Iconify icon="eva:close-fill" width={22} />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    {(type === 'Task' || type === 'Task-overView') &&
                        <TaskSidebar
                            data={data}
                            type={type}
                            overViewbtns={overViewbtns}
                            reminderDataId={reminderDataId}
                            getReminderBtn={getReminderBtn}
                            handleFollowUp={handleFollowUp}
                            handleSendWhatsApp={handleSendWhatsApp}
                            handleAttendClick={handleAttendClick}
                            handleAttendFailedClick={handleAttendFailedClick}
                            setReloadCounters={setReloadCounters}
                            onClose={onClose}
                            parentType={parentType}
                            onTaskUpdate={onTaskUpdate}
                        />}
                </Box>
            </Box>
        </SwipeableDrawer>
    );
}

ReusableSidebar.propTypes = {
    data: PropTypes.any,
    type: PropTypes.string,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    reminderDataId: PropTypes.any,
    getReminderBtn: PropTypes.func,
    handleFollowUp: PropTypes.func,
    handleSendWhatsApp: PropTypes.func,
    overViewbtns: PropTypes.func,
    handleAttendClick: PropTypes.func,
    handleAttendFailedClick: PropTypes.func,
    deleteTask: PropTypes.func,
    setReloadCounters: PropTypes.func,
    parentType: PropTypes.string,
    onTaskUpdate: PropTypes.func,
    /** When true, renders a "Legacy" badge so users can distinguish this sidebar from the V2 TaskSlideout */
    isLegacy: PropTypes.bool,
};

ReusableSidebar.defaultProps = {
    isLegacy: false,
};
